import { useCallback, useEffect, useMemo, useState } from "react"
import { Detail, Parcel, ParcelAreas, ParcelData, ParcelFeature } from "../types"
import { useGetParcelByPINQuery, useGetParcelByTMSQuery } from "../parcelsApi"

import { identify } from "@arcgis/core/rest/identify"
import IdentifyParameters from "@arcgis/core/rest/support/IdentifyParameters"
import { geodesicArea, intersect } from "@arcgis/core/geometry/geometryEngine"
import Graphic from "@arcgis/core/Graphic"
import Polygon from "@arcgis/core/geometry/Polygon"
import { getView, zoomTo } from "../../map"
import { MAP_IMAGE_LAYER_URL } from "../../map/constants"
import { useNavigate } from "react-router-dom"
import { useAppDispatch, useAppSelector } from "../../hooks"
import { selectResults, selectSelectedIndex, setSelectedIndex } from "../../search/searchSlice"
import { FetchBaseQueryError } from "@reduxjs/toolkit/query"
import { SerializedError } from "@reduxjs/toolkit"
import * as SelectionLayer from "../../map/layers/SelectionLayer"
import * as ComplexParcelDetailsLayer from "../../map/layers/ComplexParcelDetailsLayer"


export const featureSetToGraphicsArray = (set: __esri.FeatureSet): __esri.Graphic[] => {
  if (!set || !set.features.length) return []
    
  const graphics = set.features
    .map(featureGraphicToParcelDataGraphic)
    .filter(parcel => parcel !== undefined) as __esri.Graphic[]

  return graphics
}

export const featureGraphicToParcelDataGraphic = (feature: __esri.Graphic) => {
  const geometry = new Polygon({ ...feature.geometry, spatialReference: { wkid: 102100 } })
  if (!geometry) return undefined
  const attributes = parcelFeatureToParcelData(feature.attributes)
  const graphic = new Graphic({ attributes, geometry })
  return graphic
}

export const parcelFeatureToParcelData = (feature: ParcelFeature) => {
  if (!feature) return undefined
  const data = {
    OBJECTID: feature.OBJECTID,
    OwnerName: feature.OwnerName,
    PIN: feature.PIN,
    TMS: feature.TMS,
    LOT: feature.LOT,
    PARCELTYPE: feature.PARCELTYPE,
    TaxDistrict: feature.TaxDistrict,
    LegalDescr: feature.LegalDescr,
    Shape_Area: feature["Shape.STArea()"],
    Acreage: feature.Acreage,
    OwnerStreet: feature.OwnerStreet,
    OwnerCity: feature.OwnerCity,
    OwnerState: feature.OwnerState,
    OwnerZip: feature.OwnerZip,
    SaleDate: new Date(feature.SaleDate),
    Selected: false,
    DeedBook: feature.DeedBook,
    DeedPage: feature.DeedPage,
  } as ParcelData
  return data
}

export const useFillParcelAreaData = (parcel: Parcel | undefined): __esri.Graphic | undefined => {
  const parcelAreas = useIdentifyArea(parcel?.geometry)
  const processedParcelData = useMemo<ParcelData | undefined>(() => {
    if (!parcel?.attributes) return undefined
    if (!parcelAreas) return parcel.attributes

    const processedParcel = {
      ...parcel.attributes,
      zoning: parcelAreas.zoning,
      municipality: parcelAreas.municipality,
      zipcode: parcelAreas.zipcode,
      councilDistrict: parcelAreas.councilDistrict,
      councilDistrictPopulation: parcelAreas.councilDistrictPopulation,
      soilType: parcelAreas.soilType,
    } as ParcelData
    
    return processedParcel
  }, [parcel, parcelAreas])

  const processedParcel = useMemo<__esri.Graphic | undefined>(() => {
    if (!processedParcelData) return undefined
    if (!parcel?.geometry) return undefined

    return new Graphic({ attributes: processedParcelData, geometry: parcel.geometry })
  }, [processedParcelData, parcel])

  return processedParcel
}


export const useParcelByPin = (pin: string | undefined) => {
  const { data, error, isLoading } = useGetParcelByPINQuery(pin || '', {
    skip: !pin,
  })
  return useParcel(data, error, isLoading)
}
export const useParcelByTms = (tms: string | undefined) => {
  const { data, error, isLoading } = useGetParcelByTMSQuery(tms || '', {
    skip: !tms,
  })
  return useParcel(data, error, isLoading)
}

const useParcel = (data: __esri.FeatureSet | undefined, error: FetchBaseQueryError | SerializedError | undefined, isLoading: boolean) => {
  const simpleParcel = useMemo<__esri.Graphic | undefined>(() => {
    if (!data || !data.features.length) return undefined
    return featureGraphicToParcelDataGraphic(data.features[0])
  }, [data])

  useEnsureParcelSelected(simpleParcel)
  const parcel = useFillParcelAreaData(simpleParcel)

  return { parcel, error, isLoading }
}

const useEnsureParcelSelected = (parcel: __esri.Graphic | undefined) => {
  const searchResults = useAppSelector(selectResults)

  useEffect(() => {
    if (!parcel) return
    if (searchResults) return // don't select if we have search results
    setTimeout(() => SelectionLayer.selectIfNoSelection(parcel), 1000)
  }, [parcel, searchResults])
}


interface IdentifyResponse {
  results: __esri.IdentifyResult[];
  exceededTransferLimit: boolean;
}

const useIdentifyArea = (geometry: __esri.Geometry | undefined) => {
  const [retryCount, setRetry] = useState<number>(0)
  const view = getView()
  const [areas, setAreas] = useState<ParcelAreas | undefined>(undefined)
  useEffect(() => {
    if (!geometry || !view) {
      setTimeout(() => setRetry(retry => retry + 1), 1000)
      return undefined
    }
    view.when(() => {
      const params: __esri.IdentifyParameters = new IdentifyParameters()
      params.tolerance = 0
      params.layerIds = [20, 28, 30, 31, 38] // zip codes, council dist, zoning, municipalities, soils
      params.layerOption = 'all'
      params.returnGeometry = true
      params.width = view.width
      params.height = view.height
      params.mapExtent = view.extent
      params.geometry = geometry

      setAreas(undefined)
      identify(MAP_IMAGE_LAYER_URL, params).then((response: IdentifyResponse) => {
        const { results } = response
        if (!results.length) return
        
        let areas: ParcelAreas = {
          zoning: [],
          municipality: [],
          zipcode: [],
          councilDistrict: [],
          councilDistrictPopulation: undefined,
          soilType: [],
        }

        results.forEach(result => {
          const { layerId, feature, displayFieldName } = result
          const { attributes } = feature

          const polygon = intersect(geometry, feature.geometry) as __esri.Polygon
          if (!polygon) return
          const area = geodesicArea(polygon, 'square-feet')

          const detail = {
            label: attributes[displayFieldName],
            area,
            geometry: polygon,
          } as Detail

          switch (layerId) {
            case 20: // zipcodes
              areas.zipcode = areas.zipcode || []
              areas.zipcode.push(detail)
              break
            case 28: // council districts
              areas.councilDistrict = areas.councilDistrict || []
              areas.councilDistrict.push(detail)
              areas.councilDistrictPopulation = (areas.councilDistrictPopulation ?? 0) + attributes.Population
              break
            case 30: // zoning
              areas.zoning = areas.zoning || []
              areas.zoning.push(detail)
              break
            case 31: // municipalities
              areas.municipality = areas.municipality || []
              areas.municipality.push(detail)
              break
            case 38: // Soils
              areas.soilType = areas.soilType || []
              areas.soilType.push(detail)
              break
          }
        })

        if (areas.zoning?.length === 0) {
          const detail = {
            label: 'None',
            area: 1,
            geometry,
          } as Detail
          areas.zoning = [detail]
        }
        if (areas.municipality?.length === 0) {
          const detail = {
            label: 'None',
            area: 1,
            geometry,
          } as Detail
          areas.municipality = [detail]
        }
        if (areas.zipcode?.length === 0) {
          const detail = {
            label: '-',
            area: 1,
            geometry,
          } as Detail
          areas.zipcode = [detail]
        }
        if (areas.councilDistrict?.length === 0) {
          const detail = {
            label: '-',
            area: 1,
            geometry,
          } as Detail
          areas.councilDistrict = [detail]
        }
        if (areas.soilType?.length === 0) {
          const detail = {
            label: '-',
            area: 1,
            geometry,
          } as Detail
          areas.soilType = [detail]
        }

        setAreas(areas)
    })
  })

  }, [geometry, view, retryCount])

  return areas
}

export const useZoomToParcelOnLoad = (parcel: Parcel | undefined, isLoading: boolean) => {
  const geometry = parcel?.geometry
  const PIN = parcel?.attributes?.PIN

  useEffect(() => {
    if (!PIN || !geometry || isLoading) return
    zoomTo(geometry, undefined)
  }, [PIN, geometry, isLoading])
}

export const usePager = () => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const searchResults = useAppSelector(selectResults)
  const parcelSource = SelectionLayer.getSelectedParcel()?.source
  const searchResultCount = searchResults?.length || 0
  const searchResultIndex = useAppSelector(selectSelectedIndex)
  
  const count = parcelSource === "search" 
    ? searchResultCount
    : SelectionLayer.getSelectedParcelCount()
  const index = parcelSource === "search" ? searchResultIndex : SelectionLayer.getCurrentSelectionIndex()
  
  const setPage = useCallback((page: number) => {
    let pin: string | undefined = undefined
    if (parcelSource === "search") {
      const result = searchResults?.[page]
      if (!result || result.type === "Address") return
      pin = result?.PIN
      SelectionLayer.selectSearchResult(result)
      dispatch(setSelectedIndex(page))
    } else {
      SelectionLayer.setCurrentIndex(page)
      pin = SelectionLayer.getSelectedParcel()?.PIN
    }
    ComplexParcelDetailsLayer.clearGraphics()
    if (pin) {
      navigate(`/pin/${pin}`)
    }
  }, [parcelSource, navigate, dispatch, searchResults])

  return { totalPages: count, page: index, setPage }
}